import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customerService } from '../../services/customerService';
import productService, { Product } from '../../services/productService';
import { jobService } from '../../services/jobService';
import { invoiceTemplateService } from '../../services/invoiceTemplateService';
import { Estimate, EstimateFormData, EstimateItemFormData, CreateEstimateData } from '../../types/estimate';
import { Customer } from '../../types/customer';
import { Job } from '../../types/job';
import { formatCurrency } from '../../utils/formatters';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
  outline: 'none', colorScheme: 'light', boxSizing: 'border-box',
};
const inputError: React.CSSProperties = { ...inputStyle, border: '1px solid rgba(201,90,90,0.5)' };
const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase',
  color: '#6b6a5c', marginBottom: 6,
};
const errorMsg: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a', marginTop: 4,
};
const ghostBtn: React.CSSProperties = {
  padding: '10px 24px', background: 'none',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 400,
  letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: 'pointer',
};
const primaryBtn: React.CSSProperties = {
  padding: '10px 24px', background: 'linear-gradient(135deg, #d97706, #b45309)',
  border: 'none', borderRadius: 4, fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase',
  color: '#1c2033', cursor: 'pointer',
};
const dangerBtn: React.CSSProperties = {
  padding: '8px 16px', background: 'rgba(201,90,90,0.08)',
  border: '1px solid rgba(201,90,90,0.2)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 400,
  letterSpacing: '0.15em', textTransform: 'uppercase', color: '#c95a5a', cursor: 'pointer',
};
const itemRowStyle: React.CSSProperties = {
  background: '#faf7ee', border: '1px solid rgba(92,78,51,0.2)',
  borderRadius: 6, padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
};
const sectionTitle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500,
  letterSpacing: '0.3em', textTransform: 'uppercase', color: '#d97706',
};

interface EstimateFormProps {
  estimate?: Estimate;
  onSubmit: (data: CreateEstimateData) => void;
  onCancel: () => void;
}

export const EstimateForm: React.FC<EstimateFormProps> = ({ estimate, onSubmit, onCancel }) => {
  const isEditing = !!estimate;

  const getDefaultValidUntil = (issueDate: string) => {
    const date = new Date(issueDate);
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  };

  const today = new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState<EstimateFormData>({
    customerId: estimate?.customerId || '',
    jobId: estimate?.jobId || '',
    templateId: estimate?.templateId || '',
    issueDate: estimate?.issueDate ? new Date(estimate.issueDate).toISOString().split('T')[0] : today,
    validUntil: estimate?.validUntil ? new Date(estimate.validUntil).toISOString().split('T')[0] : getDefaultValidUntil(today),
    notes: estimate?.notes || '',
    items: estimate?.items?.map(item => ({
      productId: item.productId || '',
      description: item.description,
      quantity: item.quantity.toString(),
      rate: item.rate.toString(),
      taxRate: item.taxRate?.toString() || '0',
      amount: item.amount,
      taxAmount: item.taxAmount || 0
    })) || []
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: customersData } = useQuery({
    queryKey: ['customers', { limit: 100 }],
    queryFn: () => customerService.getCustomers({ limit: 100 })
  });
  const { data: productsData } = useQuery({
    queryKey: ['products', { limit: 100, isActive: true }],
    queryFn: () => productService.getProducts({ limit: 100, isActive: true })
  });
  const { data: jobsData } = useQuery({
    queryKey: ['jobs', { customerId: formData.customerId, status: 'ACTIVE', limit: 100 }],
    queryFn: () => formData.customerId
      ? jobService.getJobs({ customerId: formData.customerId, status: 'ACTIVE', limit: 100 })
      : Promise.resolve({ data: [], pagination: { currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 100 } }),
    enabled: !!formData.customerId
  });
  const { data: templatesData } = useQuery({
    queryKey: ['invoiceTemplates'],
    queryFn: () => invoiceTemplateService.getAll(false)
  });

  const customers = customersData?.data || [];
  const products: Product[] = productsData?.data || [];
  const jobs: Job[] = jobsData?.data || [];
  const templates = templatesData || [];

  const handleIssueDateChange = (newIssueDate: string) => {
    setFormData(prev => ({
      ...prev,
      issueDate: newIssueDate,
      validUntil: prev.validUntil < newIssueDate ? getDefaultValidUntil(newIssueDate) : prev.validUntil
    }));
  };

  const handleCustomerChange = (customerId: string) => {
    setFormData(prev => ({ ...prev, customerId, jobId: '' }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { productId: '', description: '', quantity: '1', rate: '0', taxRate: '0', amount: 0, taxAmount: 0 }]
    }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const updateItem = (index: number, field: keyof EstimateItemFormData, value: string | number) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      if (field === 'productId' && typeof value === 'string') {
        const p = products.find(x => x.id === value);
        if (p) { newItems[index].description = p.name; newItems[index].rate = p.unitPrice?.toString() || '0'; }
      }
      if (['quantity', 'rate', 'taxRate', 'productId'].includes(field as string)) {
        const qty = parseFloat(newItems[index].quantity) || 0;
        const rate = parseFloat(newItems[index].rate) || 0;
        const tax = parseFloat(newItems[index].taxRate) || 0;
        newItems[index].amount = qty * rate;
        newItems[index].taxAmount = (qty * rate * tax) / 100;
      }
      return { ...prev, items: newItems };
    });
  };

  const calculateTotals = useCallback(() => {
    const r = formData.items.reduce((t, i) => ({ subtotal: t.subtotal + i.amount, taxAmount: t.taxAmount + i.taxAmount, total: 0 }), { subtotal: 0, taxAmount: 0, total: 0 });
    r.total = r.subtotal + r.taxAmount;
    return r;
  }, [formData.items]);

  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.customerId) errs.customerId = 'Customer is required';
    if (!formData.templateId) errs.templateId = 'Template is required';
    if (!formData.validUntil) {
      errs.validUntil = 'Valid until date is required';
    } else {
      if (new Date(formData.validUntil) <= new Date(formData.issueDate)) errs.validUntil = 'Must be after issue date';
      if (new Date(formData.validUntil) <= new Date(today)) errs.validUntil = 'Must be a future date';
    }
    if (formData.items.length === 0) errs.items = 'At least one item is required';
    formData.items.forEach((item, i) => {
      if (!item.description.trim()) errs[`items.${i}.description`] = 'Required';
      if (parseFloat(item.quantity) <= 0) errs[`items.${i}.quantity`] = 'Must be > 0';
      if (parseFloat(item.rate) <= 0) errs[`items.${i}.rate`] = 'Must be > 0';
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    const submitData: CreateEstimateData = {
      customerId: formData.customerId,
      jobId: formData.jobId || undefined,
      templateId: formData.templateId,
      issueDate: formData.issueDate,
      validUntil: formData.validUntil,
      notes: formData.notes || undefined,
      items: formData.items.map(item => ({
        productId: item.productId || undefined,
        description: item.description,
        quantity: parseFloat(item.quantity),
        rate: parseFloat(item.rate),
        taxRate: parseFloat(item.taxRate) || undefined
      }))
    };
    console.log('Frontend submitting estimate data:', JSON.stringify(submitData, null, 2));
    onSubmit(submitData);
  };

  const totals = useMemo(() => calculateTotals(), [calculateTotals]);

  return (
    <div style={{
      maxWidth: 900, margin: '0 auto',
      background: '#ffffff', border: '1px solid rgba(92,78,51,0.22)',
      borderRadius: 8, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '20px 28px', borderBottom: '1px solid rgba(92,78,51,0.15)',
      }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.35em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 6 }}>
            {isEditing ? 'Edit' : 'New'}
          </div>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontSize: 24, fontWeight: 300, color: '#1c2033', margin: 0 }}>
            {isEditing ? 'Edit Estimate' : 'Create Estimate'}
          </h2>
        </div>
        <button type="button" onClick={onCancel} style={ghostBtn}>Cancel</button>
      </div>

      <form onSubmit={handleSubmit} style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* Row 1: Customer, Job, Template, Issue Date */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <div>
            <label htmlFor="customerId" style={labelStyle}>Customer <span style={{ color: '#c95a5a' }}>*</span></label>
            <select
              id="customerId"
              value={formData.customerId}
              onChange={(e) => handleCustomerChange(e.target.value)}
              style={errors.customerId ? inputError : inputStyle}
            >
              <option value="">Select a customer</option>
              {customers.map((c: Customer) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {errors.customerId && <p style={errorMsg}>{errors.customerId}</p>}
          </div>

          <div>
            <label htmlFor="jobId" style={labelStyle}>
              Job <span style={{ color: '#8a8778', fontFamily: "'Inter', sans-serif", textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
            </label>
            <select
              id="jobId"
              value={formData.jobId}
              onChange={(e) => setFormData(prev => ({ ...prev, jobId: e.target.value }))}
              disabled={!formData.customerId}
              style={{ ...inputStyle, opacity: !formData.customerId ? 0.5 : 1 }}
            >
              <option value="">Select a job</option>
              {jobs.map((j: Job) => <option key={j.id} value={j.id}>{j.jobNumber} - {j.jobName}</option>)}
            </select>
            {!formData.customerId && <p style={{ ...errorMsg, color: '#8a8778' }}>Select a customer first</p>}
          </div>

          <div>
            <label htmlFor="templateId" style={labelStyle}>Template <span style={{ color: '#c95a5a' }}>*</span></label>
            <select
              id="templateId"
              value={formData.templateId}
              onChange={(e) => setFormData(prev => ({ ...prev, templateId: e.target.value }))}
              style={errors.templateId ? inputError : inputStyle}
            >
              <option value="">Select a template</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name} — {t.templateCompanyName}</option>)}
            </select>
            {errors.templateId && <p style={errorMsg}>{errors.templateId}</p>}
          </div>

          <div>
            <label htmlFor="issueDate" style={labelStyle}>Issue Date</label>
            <input type="date" id="issueDate" value={formData.issueDate} onChange={(e) => handleIssueDateChange(e.target.value)} style={inputStyle} />
          </div>
        </div>

        {/* Row 2: Valid Until */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <div>
            <label htmlFor="validUntil" style={labelStyle}>Valid Until <span style={{ color: '#c95a5a' }}>*</span></label>
            <input
              type="date"
              id="validUntil"
              value={formData.validUntil}
              onChange={(e) => setFormData(prev => ({ ...prev, validUntil: e.target.value }))}
              style={errors.validUntil ? inputError : inputStyle}
            />
            {errors.validUntil && <p style={errorMsg}>{errors.validUntil}</p>}
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: 'rgba(92,78,51,0.15)' }} />

        {/* Items Section */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={sectionTitle}>Line Items</div>
            <button type="button" onClick={addItem} style={{ ...primaryBtn, display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              Add Item
            </button>
          </div>

          {errors.items && <p style={{ ...errorMsg, marginBottom: 12 }}>{errors.items}</p>}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {formData.items.map((item, index) => (
              <div key={index} style={itemRowStyle}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Product <span style={{ color: '#8a8778', fontFamily: "'Inter', sans-serif", textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                    <select value={item.productId} onChange={(e) => updateItem(index, 'productId', e.target.value)} style={inputStyle}>
                      <option value="">Select a product</option>
                      {products.map((p: Product) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button type="button" onClick={() => removeItem(index)} style={dangerBtn}>Remove</button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>Quantity <span style={{ color: '#c95a5a' }}>*</span></label>
                    <input
                      type="number" min="0" step="0.01"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      style={errors[`items.${index}.quantity`] ? inputError : inputStyle}
                    />
                    {errors[`items.${index}.quantity`] && <p style={errorMsg}>{errors[`items.${index}.quantity`]}</p>}
                  </div>
                  <div>
                    <label style={labelStyle}>Rate <span style={{ color: '#c95a5a' }}>*</span></label>
                    <input
                      type="number" min="0" step="0.01"
                      value={item.rate}
                      onChange={(e) => updateItem(index, 'rate', e.target.value)}
                      style={errors[`items.${index}.rate`] ? inputError : inputStyle}
                    />
                    {errors[`items.${index}.rate`] && <p style={errorMsg}>{errors[`items.${index}.rate`]}</p>}
                  </div>
                  <div>
                    <label style={labelStyle}>Tax Rate (%)</label>
                    <input type="number" min="0" step="0.01" value={item.taxRate} onChange={(e) => updateItem(index, 'taxRate', e.target.value)} style={inputStyle} />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Description <span style={{ color: '#c95a5a' }}>*</span></label>
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    placeholder="Enter item description"
                    style={errors[`items.${index}.description`] ? inputError : inputStyle}
                  />
                  {errors[`items.${index}.description`] && <p style={errorMsg}>{errors[`items.${index}.description`]}</p>}
                </div>

                <div style={{
                  background: 'rgba(241,245,249,1)', border: '1px solid rgba(92,78,51,0.15)',
                  borderRadius: 4, padding: '8px 12px',
                  fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c',
                }}>
                  Amount: <span style={{ color: '#5b5a52' }}>{formatCurrency(item.amount)}</span>
                  &nbsp;&nbsp;Tax: <span style={{ color: '#5b5a52' }}>{formatCurrency(item.taxAmount)}</span>
                  &nbsp;&nbsp;Total: <span style={{ color: '#d97706', fontWeight: 500 }}>{formatCurrency(item.amount + item.taxAmount)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" style={labelStyle}>Notes</label>
          <textarea
            id="notes"
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Add any additional notes..."
            style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
          />
        </div>

        {/* Totals */}
        {formData.items.length > 0 && (
          <div style={{
            background: 'rgba(241,245,249,1)', border: '1px solid rgba(92,78,51,0.22)',
            borderRadius: 6, padding: '20px 24px',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c' }}>Subtotal</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: '#5b5a52' }}>{formatCurrency(totals.subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c' }}>Tax</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: '#5b5a52' }}>{formatCurrency(totals.taxAmount)}</span>
              </div>
              <div style={{ height: 1, background: 'rgba(92,78,51,0.25)', margin: '4px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#d97706' }}>Total</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 28, fontWeight: 300, color: '#fbbf24' }}>{formatCurrency(totals.total)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 8 }}>
          <button type="button" onClick={onCancel} style={ghostBtn}>Cancel</button>
          <button type="submit" style={primaryBtn}>
            {isEditing ? 'Update Estimate' : 'Create Estimate'}
          </button>
        </div>
      </form>
    </div>
  );
};
